import { useEffect, useRef } from 'react';

import {
  trackAdPlatformConnected,
  type AdPlatformConversion,
} from '@/lib/analytics';
import {
  broadcastResult,
  OAUTH_POPUP_FLAG,
  type OAuthStatus,
} from '@/components/connectors/oauth-message';

export const isOAuthPopup = (): boolean => {
  try {
    if (typeof window === 'undefined') return false;
    if (window.opener) return true;
    return window.sessionStorage.getItem(OAUTH_POPUP_FLAG) === 'true';
  } catch {
    return !!window.opener;
  }
};

const CLOSE_DELAY_MS = 600;

interface OAuthCallbackParams {
  status: OAuthStatus | null;
  message?: string | null;
  // Set where the callback itself completed the link and won the platform's claim.
  conversion?: AdPlatformConversion;
}

export function useOAuthCallback({
  status,
  message,
  conversion,
}: OAuthCallbackParams): void {
  const ranRef = useRef(false);

  useEffect(() => {
    if (!status || status === 'select_account') return;
    if (ranRef.current) return;
    if (!isOAuthPopup()) {
      // A full-page flow landed here itself, so this window reports its own link.
      if (status === 'success' && conversion)
        trackAdPlatformConnected(conversion);
      return;
    }
    ranRef.current = true;

    broadcastResult(status, message, conversion);
    setTimeout(() => {
      window.close();
    }, CLOSE_DELAY_MS);
  }, [status, message, conversion]);
}
